<div class="page-container">
  @if (addressLoading()) {
    <div class="loading-container flex flex-column flex-align-center p-lg">
      <app-spinner />
    </div>
  } @else if (!address()) {
    <app-card>
      <app-card-content>
        <div class="empty-state p-lg" data-testid="address-not-found">
          <app-icon name="circle-alert" [size]="48" />
          <p>{{ 'addresses.form.notFoundError' | translate }}</p>
        </div>
      </app-card-content>
    </app-card>
  } @else {
    @let a = address()!;

    <app-card>
      <app-card-header>
        <app-card-title>
          <div class="header-row flex flex-justify-space-between flex-align-center gap-sm">
            <div class="flex flex-align-center gap-sm">
              <app-button variant="icon" (click)="goBack()" [attr.aria-label]="'addresses.detail.back' | translate"
                data-testid="address-detail-back-button">
                <app-icon name="arrow-left" [size]="20" />
              </app-button>
              <div class="flex flex-column gap-xs">
                <span data-testid="address-detail-formatted">{{ a.formatted_address }}</span>
                @if (a.business_name) {
                  <span class="subtitle" data-testid="address-detail-business">{{ a.business_name }}</span>
                }
              </div>
            </div>
            <app-button variant="outlined" [routerLink]="['/map/addresses', a.id, 'edit']"
              *hasPermission="'address.update'" data-testid="edit-address-button">
              <app-icon name="pencil" [size]="16" /> {{ 'addresses.detail.edit' | translate }}
            </app-button>
          </div>
        </app-card-title>
      </app-card-header>

      <app-card-content>
        <div class="address-facts flex gap-md">
          <span class="fact">{{ 'addresses.form.placeTypeLabel' | translate }}: {{ 'addresses.placeType.' + a.place_type | translate }}</span>
          @if (a.unit) {
            <span class="fact">{{ 'addresses.form.unitLabel' | translate }}: {{ a.unit }}</span>
          }
          @if (a.last_interaction_at) {
            <span class="fact">{{ 'addresses.detail.lastInteraction' | translate }}: {{ a.last_interaction_at | relativeTime: now() }}</span>
          }
        </div>
        @if (a.notes) {
          <p class="address-notes" data-testid="address-detail-notes">{{ a.notes }}</p>
        }

        @if (a.do_not_contact) {
          <div class="dnc-banner flex flex-align-center gap-sm p-md" data-testid="dnc-banner">
            <app-icon name="ban" [size]="20" />
            <div class="flex flex-column flex-grow">
              <strong>{{ 'dnc.banner.title' | translate }}</strong>
              @if (a.do_not_contact_reason) {
                <span class="dnc-reason">{{ a.do_not_contact_reason }}</span>
              }
            </div>
            <app-button variant="text" (click)="openClearDnc()" *hasPermission="'address.manage-dnc'"
              data-testid="clear-dnc-button">
              {{ 'dnc.banner.clear' | translate }}
            </app-button>
          </div>
        }

        <div class="actions flex gap-sm mt-md">
          <app-button variant="filled" (click)="logInteraction()" *hasPermission="'interaction.create'"
            data-testid="log-interaction-button">
            <app-icon name="plus" [size]="16" /> {{ 'interactions.log' | translate }}
          </app-button>
        </div>

        <h3 class="history-heading">{{ 'addresses.detail.history' | translate }}</h3>

        @if (timeline().length === 0) {
          <div class="empty-state p-lg" data-testid="timeline-empty">
            <app-icon name="history" [size]="40" />
            <p>{{ 'addresses.detail.noHistory' | translate }}</p>
          </div>
        } @else {
          <ol class="timeline" data-testid="timeline">
            @for (item of timeline(); track item.kind + (item.kind === 'interaction' ? item.interaction.id : item.entry.id)) {
              @if (item.kind === 'interaction') {
                @let i = item.interaction;
                <li class="timeline-item" [attr.data-testid]="'timeline-interaction-' + i.id">
                  <div class="timeline-row flex flex-justify-space-between flex-align-center gap-sm">
                    <div class="flex flex-align-center gap-sm">
                      <app-outcome-chip [outcome]="i.outcome_kind" [objectionId]="i.objection_id" />
                      <span class="timeline-type">{{ typeLabels().get(i.interaction_type) ?? i.interaction_type }}</span>
                    </div>
                    <a class="timeline-edit" [routerLink]="interactionEditLink(i)"
                      *hasPermission="'interaction.update'" [attr.data-testid]="'edit-interaction-' + i.id">
                      <app-icon name="pencil" [size]="14" />
                    </a>
                  </div>
                  @if (i.outcome_kind === 'objection' && i.objection_id) {
                    <div class="timeline-detail">{{ objectionLabels().get(i.objection_id) ?? i.objection_id }}</div>
                  }
                  @if (i.follow_up_at) {
                    <div class="timeline-detail">
                      <app-icon name="clock" [size]="14" /> {{ 'interactions.followUp' | translate }}: {{ i.follow_up_at | dateTimezone }}
                    </div>
                  }
                  @if (i.notes) {
                    <div class="timeline-notes">{{ i.notes }}</div>
                  }
                  @if (tagsByInteraction().get(i.id); as tags) {
                    <div class="timeline-tags flex gap-xs" [attr.data-testid]="'timeline-tags-' + i.id">
                      @for (tag of tags; track tag.id) {
                        <app-tag-chip [tag]="tag" />
                      }
                    </div>
                  }
                  <app-attachment-strip entityType="interaction" [entityId]="i.id" />
                  <div class="timeline-meta">
                    {{ userNames().get(i.created_by) ?? '' }} · {{ i.occurred_at | dateTimezone }}
                  </div>
                </li>
              } @else {
                <li class="timeline-item timeline-change" [attr.data-testid]="'timeline-change-' + item.entry.id">
                  <div class="timeline-row flex flex-align-center gap-sm">
                    <app-icon name="pencil-line" [size]="14" />
                    <span class="timeline-type">{{ 'addresses.detail.edited' | translate }}</span>
                  </div>
                  <ul class="change-list">
                    @for (field of changeFields(item.changes); track field) {
                      <li>
                        <span class="change-field">{{ 'addresses.fields.' + field | translate }}</span>:
                        <span class="change-from">{{ changeValue(item.changes[field].from) }}</span>
                        <app-icon name="arrow-right" [size]="12" />
                        <span class="change-to">{{ changeValue(item.changes[field].to) }}</span>
                      </li>
                    }
                  </ul>
                  <div class="timeline-meta">
                    {{ userNames().get(item.entry.changed_by) ?? '' }} · {{ item.entry.changed_at | dateTimezone }}
                  </div>
                </li>
              }
            }
          </ol>
        }
      </app-card-content>
    </app-card>
  }
</div>

@if (showClearDncDialog()) {
  <app-modal-dialog (backdropClick)="cancelClearDnc()">
    <div header>
      <div class="heading-md">{{ 'dnc.clearDialog.title' | translate }}</div>
    </div>
    <div content>
      <p class="modal-description">{{ 'dnc.clearDialog.description' | translate }}</p>
    </div>
    <div footer>
      <app-button variant="text" (click)="cancelClearDnc()" [disabled]="clearingDnc()" data-testid="cancel-clear-dnc-button">
        {{ 'dnc.clearDialog.cancel' | translate }}
      </app-button>
      <app-button variant="filled" color="primary" (click)="confirmClearDnc()" [disabled]="clearingDnc()"
        data-testid="confirm-clear-dnc-button">
        {{ 'dnc.clearDialog.confirm' | translate }}
      </app-button>
    </div>
  </app-modal-dialog>
}
